import { useState, useEffect } from 'react';
import {
  Table, Button, Space, Tag, Modal, Form, Input, Select, message, Popconfirm, Tooltip, Radio
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, KeyOutlined, FilterOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '../api/users';
import { firmsApi } from '../api/firms';
import type { PanelUser, CreateUserRequest, UpdateUserRequest, Firm, FirmRole } from '../types';
import { EpostaHucresi, ParolaBaglantisiButonu } from '../components/users/ParolaBaglantisi';
import { PAROLA_KURALI_IPUCU, parolaKuraliRule } from '../utils/parolaKurali';

type ParolaYontemi = 'davet' | 'elle';
type UserFormValues = CreateUserRequest & { isActive?: boolean; parolaYontemi?: ParolaYontemi };

export function Users() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<PanelUser | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [firmFilter, setFirmFilter] = useState<number | undefined>(undefined);
  const [selectedFirmId, setSelectedFirmId] = useState<number | undefined>(undefined);
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const queryClient = useQueryClient();

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users', firmFilter],
    queryFn: () => usersApi.getAll(firmFilter),
  });

  const { data: firms = [] } = useQuery({
    queryKey: ['firms'],
    queryFn: firmsApi.getAll,
  });

  // Fetch firm roles when a firm is selected
  const { data: firmRoles = [] } = useQuery({
    queryKey: ['firm-roles', selectedFirmId],
    queryFn: () => selectedFirmId ? firmsApi.getRoles(selectedFirmId) : Promise.resolve([]),
    enabled: !!selectedFirmId,
  });

  // Update selectedFirmId when form changes
  useEffect(() => {
    if (editingUser?.firmId) {
      setSelectedFirmId(editingUser.firmId);
    }
  }, [editingUser]);

  const createMutation = useMutation({
    mutationFn: usersApi.create,
    onSuccess: (created: PanelUser) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      if (created?.davetGonderildi === true) {
        message.success(`Davet e-postası gönderildi: ${created.email}`);
      } else if (created?.davetGonderildi === false) {
        message.warning(
          "Kullanıcı açıldı ama davet e-postası gönderilemedi; listeden 'Bağlantı gönder' ile tekrar deneyin.",
          8,
        );
      } else {
        message.success('Kullanıcı oluşturuldu');
      }
      setIsModalOpen(false);
      form.resetFields();
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      message.error(error.response?.data?.message || 'Kullanıcı oluşturulamadı');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateUserRequest }) =>
      usersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      message.success('Kullanıcı güncellendi');
      setIsModalOpen(false);
      setEditingUser(null);
      form.resetFields();
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      message.error(error.response?.data?.message || 'Kullanıcı güncellenemedi');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: usersApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      message.success('Kullanıcı silindi');
    },
    onError: () => message.error('Kullanıcı silinemedi'),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, password }: { id: number; password: string }) =>
      usersApi.resetPassword(id, password),
    onSuccess: () => {
      message.success('Şifre sıfırlandı');
      setIsPasswordModalOpen(false);
      setSelectedUserId(null);
      passwordForm.resetFields();
    },
    onError: () => message.error('Şifre sıfırlanamadı'),
  });

  const parolaYontemi: ParolaYontemi = Form.useWatch('parolaYontemi', form) ?? 'davet';

  const handleSubmit = (values: UserFormValues) => {
    const { parolaYontemi: yontem, password, ...rest } = values;
    if (editingUser) {
      updateMutation.mutate({ id: editingUser.id, data: rest });
    } else if (yontem === 'elle') {
      createMutation.mutate({ ...rest, password });
    } else {
      // Davet yolu: parola HİÇ gönderilmez; API rastgele parola koyup davet e-postası yollar.
      createMutation.mutate(rest);
    }
  };

  const handleEdit = (user: PanelUser) => {
    setEditingUser(user);
    setSelectedFirmId(user.firmId || undefined);
    form.setFieldsValue({
      ...user,
      firmId: user.firmId || undefined,
      firmRoleId: user.firmRoleId || undefined,
    });
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setEditingUser(null);
    setSelectedFirmId(undefined);
    form.resetFields();
    form.setFieldsValue({ role: 'User', parolaYontemi: 'davet' });
    setIsModalOpen(true);
  };

  const handleFirmChange = (firmId: number | undefined) => {
    setSelectedFirmId(firmId);
    form.setFieldValue('firmRoleId', undefined);
  };

  const handleResetPassword = (userId: number) => {
    setSelectedUserId(userId);
    passwordForm.resetFields();
    setIsPasswordModalOpen(true);
  };

  const columns = [
    {
      title: 'Ad Soyad',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'E-posta',
      dataIndex: 'email',
      key: 'email',
      render: (_: string, record: PanelUser) => <EpostaHucresi user={record} />,
    },
    {
      title: 'Firma',
      dataIndex: 'firmName',
      key: 'firmName',
      render: (firmName: string | null) => firmName || <Tag>Yönetici</Tag>,
    },
    {
      title: 'Sistem Rolü',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => (
        <Tag color={role === 'SuperAdmin' ? 'red' : 'blue'}>
          {role === 'SuperAdmin' ? 'Yönetici' : 'Kullanıcı'}
        </Tag>
      ),
    },
    {
      title: 'Firma Rolü',
      dataIndex: 'firmRoleName',
      key: 'firmRoleName',
      render: (roleName: string | null, record: PanelUser) =>
        record.firmId ? (roleName || <Tag color="orange">Atanmamış</Tag>) : '-',
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? 'Aktif' : 'Pasif'}
        </Tag>
      ),
    },
    {
      title: 'Son Giriş',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      render: (date: string | null) =>
        date ? new Date(date).toLocaleString('tr-TR') : '-',
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: unknown, record: PanelUser) => (
        <Space>
          <Tooltip title="Düzenle">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Şifre Sıfırla">
            <Button
              type="text"
              icon={<KeyOutlined />}
              onClick={() => handleResetPassword(record.id)}
            />
          </Tooltip>
          <ParolaBaglantisiButonu user={record} />
          <Popconfirm
            title="Kullanıcıyı silmek istediğinize emin misiniz?"
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2>Panel Kullanıcıları</h2>
        <Space>
          <Select
            placeholder="Firma Filtrele"
            allowClear
            style={{ width: 200 }}
            value={firmFilter}
            onChange={setFirmFilter}
            options={[
              { value: undefined, label: 'Tüm Firmalar' },
              ...firms.map((f: Firm) => ({ value: f.id, label: f.name })),
            ]}
            suffixIcon={<FilterOutlined />}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Yeni Kullanıcı
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={isLoading}
      />

      {/* Create/Edit Modal */}
      <Modal
        title={editingUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı'}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingUser(null);
          setSelectedFirmId(undefined);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Ad Soyad"
            rules={[{ required: true, message: 'Ad soyad gerekli' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="E-posta"
            extra={editingUser ? 'Adres değişirse doğrulaması sıfırlanır.' : undefined}
            rules={[
              { required: true, message: 'E-posta gerekli' },
              { type: 'email', message: 'Geçerli bir e-posta girin' },
            ]}
          >
            <Input />
          </Form.Item>

          {!editingUser && (
            <Form.Item name="parolaYontemi" label="Parola" initialValue="davet">
              <Radio.Group>
                <Space orientation="vertical">
                  <Radio value="davet">Davet e-postası gönder (önerilen)</Radio>
                  <Radio value="elle">Parolayı ben belirleyeyim</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          )}

          {!editingUser && parolaYontemi === 'elle' && (
            <Form.Item
              name="password"
              label="Şifre"
              extra={PAROLA_KURALI_IPUCU}
              rules={[{ required: true, message: 'Şifre gerekli' }, parolaKuraliRule]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
          )}

          <Form.Item
            name="firmId"
            label="Firma"
            extra="Boş bırakılırsa yönetici hesabı olur"
          >
            <Select
              placeholder="Firma seçin"
              allowClear
              options={firms.map((f: Firm) => ({ value: f.id, label: f.name }))}
              onChange={handleFirmChange}
            />
          </Form.Item>

          {selectedFirmId && (
            <Form.Item
              name="firmRoleId"
              label="Firma Rolü"
              extra="Kullanıcının rapor erişim yetkilerini belirler"
            >
              <Select
                placeholder="Rol seçin"
                allowClear
                options={firmRoles.map((r: FirmRole) => ({
                  value: r.id,
                  label: `${r.name}${r.isDefault ? ' (Varsayılan)' : ''}`,
                }))}
              />
            </Form.Item>
          )}

          <Form.Item
            name="role"
            label="Sistem Rolü"
            rules={[{ required: true, message: 'Rol gerekli' }]}
          >
            <Select
              options={[
                { value: 'User', label: 'Kullanıcı' },
                { value: 'SuperAdmin', label: 'Yönetici (SuperAdmin)' },
              ]}
            />
          </Form.Item>

          {editingUser && (
            <Form.Item
              name="isActive"
              label="Durum"
            >
              <Select
                options={[
                  { value: true, label: 'Aktif' },
                  { value: false, label: 'Pasif' },
                ]}
              />
            </Form.Item>
          )}

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createMutation.isPending || updateMutation.isPending}
              >
                {editingUser ? 'Güncelle' : 'Oluştur'}
              </Button>
              <Button onClick={() => setIsModalOpen(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Password Reset Modal */}
      <Modal
        title="Şifre Sıfırla"
        open={isPasswordModalOpen}
        onCancel={() => {
          setIsPasswordModalOpen(false);
          setSelectedUserId(null);
          passwordForm.resetFields();
        }}
        footer={null}
        width={400}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={(values) => {
            if (selectedUserId) {
              resetPasswordMutation.mutate({
                id: selectedUserId,
                password: values.newPassword,
              });
            }
          }}
        >
          <Form.Item
            name="newPassword"
            label="Yeni Şifre"
            extra={PAROLA_KURALI_IPUCU}
            rules={[{ required: true, message: 'Şifre gerekli' }, parolaKuraliRule]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Şifre Tekrar"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Şifre tekrarı gerekli' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Şifreler eşleşmiyor'));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={resetPasswordMutation.isPending}
              >
                Şifreyi Sıfırla
              </Button>
              <Button onClick={() => setIsPasswordModalOpen(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
